import React, { useEffect } from "react";
import { useSystemSetting } from "../../utils/Hooks";
import { isOriginAllowed } from "../../utils/Url";

const getTargetOrigin = (
  allowedOriginPattern: string | undefined
): string | undefined => {
  const targetOrigin = document.referrer;

  if (!window.parent || window === window.parent || !targetOrigin) {
    console.warn("No document.referrer found. Message will not be sent.");
    return;
  }

  // Detect and validate the referrer origin
  let referrerOrigin: string;
  try {
    referrerOrigin = new URL(targetOrigin).origin;
  } catch (e) {
    console.warn("Invalid document.referrer URL:", e);
    return;
  }

  // Get allowed origin pattern from client settings
  if (!allowedOriginPattern) {
    console.warn(
      "No allowed origin pattern configured. Message will not be sent."
    );
    return;
  }

  // Validate the referrer origin
  if (!isOriginAllowed(referrerOrigin, allowedOriginPattern)) {
    console.warn(
      `Referrer origin "${referrerOrigin}" is not allowed. Message will not be sent.`
    );
    return;
  }

  return referrerOrigin;
};

export const useSendHeightToParent = (
  ref: React.RefObject<HTMLDivElement>,
  messageType: string,
  addHeight: number = 0
) => {
  const allowedOriginPattern = useSystemSetting(
    "REACT_APP_POST_MESSAGE_ALLOWED_ORIGIN"
  );
  const postMessageTargetOrigin = getTargetOrigin(allowedOriginPattern);

  useEffect(() => {
    if (!postMessageTargetOrigin) {
      return;
    }
    let resizeObserver: ResizeObserver | null = null;
    const currentRef = ref.current;

    // Function to send component height to parent window via postMessage
    const sendHeightToParent = () => {
      if (window.parent && currentRef && window !== window.parent) {
        const elementHeight = currentRef.scrollHeight + addHeight;

        if (elementHeight !== undefined) {
          // Post message to parent with current component height
          window.parent.postMessage(
            {
              type: messageType,
              data: {
                height: elementHeight
              }
            },
            postMessageTargetOrigin
          );
        }
      }
    };

    if (currentRef) {
      // Send initial height measurement
      sendHeightToParent();

      // Set up ResizeObserver to watch for component size changes
      resizeObserver = new ResizeObserver(sendHeightToParent);
      resizeObserver.observe(currentRef);
    }

    // Cleanup function to disconnect observer
    return () => {
      if (resizeObserver && currentRef) {
        resizeObserver.unobserve(currentRef);
        resizeObserver.disconnect();
      }
    };
  }, [ref, messageType, postMessageTargetOrigin, addHeight]);
};
